import {
  Path,
  PathValue,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import {
  DateTimePicker,
  DateTimePickerProps,
} from 'components/atoms/DateTimePicker';
import { ChangeEvent } from 'react';

type TaskStartDateTimePickerProps<TFieldValues extends FieldValues> = Pick<
  DateTimePickerProps<TFieldValues>,
  'inputProps'
> & {
  name: FieldPath<TFieldValues>;
  control: UseFormReturn<TFieldValues>['control'];
  endDateTimeProps: EndDateTimeProps<TFieldValues>;
};

type EndDateTimeProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  getValues: UseFormReturn<TFieldValues>['getValues'];
  setValue: UseFormReturn<TFieldValues>['setValue'];
  trigger: UseFormReturn<TFieldValues>['trigger'];
};

const decreaseOneHour = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours() - 1,
    date.getMinutes(),
    0,
  );

const increaseOneHour = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours() + 1,
    date.getMinutes(),
    0,
  );

const onChangeStartDate = async <TFieldValues extends FieldValues>(
  event: ChangeEvent<HTMLInputElement>,
  { name, getValues, setValue, trigger }: EndDateTimeProps<TFieldValues>,
) => {
  const startDate = new Date(event.target.value);
  const endDate = getValues(name) as Date;
  if (startDate == null) return;
  if (
    endDate == null ||
    startDate.getTime() > decreaseOneHour(endDate).getTime()
  ) {
    setValue(
      name,
      increaseOneHour(startDate) as PathValue<TFieldValues, Path<TFieldValues>>,
    );
  }
  await trigger(name);
};

/**
 * リリースタスク作成時の日時入力項目です。
 */
export const TaskStartDateTimePicker = <TFieldValues extends FieldValues>({
  name,
  control,
  inputProps,
  endDateTimeProps,
}: TaskStartDateTimePickerProps<TFieldValues>) => (
  <DateTimePicker
    datePickerProps={{
      minDate: new Date(),
      timeIntervals: 60,
    }}
    controllerProps={{
      control,
      name,
      rules: {
        required: '必須項目です。',
        onChange: (event: ChangeEvent<HTMLInputElement>) =>
          onChangeStartDate(event, { ...endDateTimeProps }),
      },
    }}
    inputProps={inputProps}
  />
);
